import React, { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  ArrowRight,
  Brain,
  Calculator,
  Trophy,
  RefreshCcw,
  CheckCircle,
  XCircle,
  Info,
  Delete,
} from "lucide-react";

// --- Helper Functions ---

// Hàm định dạng số hiển thị với dấu phẩy
const formatNumber = (num: number | string): string => {
  if (num === "" || num === null || num === undefined) return "";
  // Chuyển dấu chấm thành dấu phẩy
  return num.toString().replace(".", ",");
};

// Hàm chuyển chuỗi nhập vào (có dấu phẩy) thành số để tính toán
const parseNumber = (str: string): number => {
  if (!str) return 0;
  // Chuyển dấu phẩy về dấu chấm để JS hiểu
  const normalized = str.replace(/,/g, ".");
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
};

const roundToPlace = (num: number, place: string) => {
  let factor = 1;
  switch (place) {
    case "thousand":
      factor = 0.001;
      break; // Hàng nghìn
    case "hundred":
      factor = 0.01;
      break; // Hàng trăm
    case "ten":
      factor = 0.1;
      break; // Hàng chục
    case "integer":
      factor = 1;
      break; // Hàng đơn vị
    case "tenth":
      factor = 10;
      break; // Phần mười
    case "hundredth":
      factor = 100;
      break; // Phần trăm
    case "thousandth":
      factor = 1000;
      break; // Phần nghìn
  }
  // Math.round chỉ làm việc tốt với số nguyên, nên ta nhân lên rồi chia xuống
  return Math.round(num * factor) / factor;
};

const getFloorCeil = (num: number, place: string) => {
  let factor = 1;
  switch (place) {
    case "thousand":
      factor = 0.001;
      break;
    case "hundred":
      factor = 0.01;
      break;
    case "ten":
      factor = 0.1;
      break;
    case "integer":
      factor = 1;
      break;
    case "tenth":
      factor = 10;
      break;
    case "hundredth":
      factor = 100;
      break;
    case "thousandth":
      factor = 1000;
      break;
  }

  const floor = Math.floor(num * factor) / factor;
  const ceil = Math.ceil(num * factor) / factor;

  // Xử lý trường hợp số đã tròn sẵn (ví dụ làm tròn 30 đến hàng chục)
  // Ta vẫn cần hiển thị khoảng (ví dụ 20 - 30 hoặc 30 - 40) để mô hình hoạt động
  if (floor === ceil) {
    // Nếu số là 30, floor=30, ceil=30. Ta muốn hiển thị khoảng 30 -> 40
    return { floor: floor, ceil: floor + 1 / factor };
  }
  return { floor, ceil };
};

// --- Components ---

export default function RoundingApp() {
  const [activeTab, setActiveTab] = useState<"learn" | "explore" | "practice">(
    "learn"
  );

  return (
    <div className="min-h-screen bg-blue-50 font-sans text-slate-800">
      {/* Header */}
      <header className="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-yellow-300" />
            <h1 className="text-xl md:text-2xl font-bold">
              Phép Thuật Làm Tròn Số
            </h1>
          </div>
          <div className="hidden md:block text-indigo-200 text-sm font-medium">
            Toán Lớp 5 (Cánh Diều / Chân Trời ST / Kết Nối TT)
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white shadow-sm mb-6 sticky top-[64px] z-40">
        <div className="max-w-5xl mx-auto flex">
          <button
            onClick={() => setActiveTab("learn")}
            className={`flex-1 py-3 md:py-4 text-center font-bold text-base md:text-lg transition-colors border-b-4 ${
              activeTab === "learn"
                ? "border-indigo-600 text-indigo-600 bg-indigo-50"
                : "border-transparent hover:bg-gray-50"
            }`}
          >
            <span className="flex justify-center items-center gap-2">
              <Info size={20} />{" "}
              <span className="hidden sm:inline">Bài Học</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("explore")}
            className={`flex-1 py-3 md:py-4 text-center font-bold text-base md:text-lg transition-colors border-b-4 ${
              activeTab === "explore"
                ? "border-green-600 text-green-600 bg-green-50"
                : "border-transparent hover:bg-gray-50"
            }`}
          >
            <span className="flex justify-center items-center gap-2">
              <Calculator size={20} />{" "}
              <span className="hidden sm:inline">Mô Hình</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("practice")}
            className={`flex-1 py-3 md:py-4 text-center font-bold text-base md:text-lg transition-colors border-b-4 ${
              activeTab === "practice"
                ? "border-orange-500 text-orange-500 bg-orange-50"
                : "border-transparent hover:bg-gray-50"
            }`}
          >
            <span className="flex justify-center items-center gap-2">
              <Trophy size={20} />{" "}
              <span className="hidden sm:inline">Luyện Tập</span>
            </span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto p-4 pb-20">
        {activeTab === "learn" && <LearnSection />}
        {activeTab === "explore" && <ExploreSection />}
        {activeTab === "practice" && <PracticeSection />}
      </main>

      <footer className="bg-slate-200 text-center py-4 mt-8 text-slate-500 text-sm">
        Dành tặng các em học sinh lớp 5 thân yêu!
      </footer>

      {/* Watermark / Bản quyền */}
      <div className="fixed bottom-1 right-2 text-slate-400 text-xs font-bold opacity-30 pointer-events-none select-none z-50">
        Chauphan
      </div>
    </div>
  );
}

// --- 1. Learn Section ---
// (Giữ nguyên phần bài học vì nó là lý thuyết chung)
function LearnSection() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white p-6 rounded-2xl shadow-md border-l-8 border-indigo-500">
        <h2 className="text-2xl font-bold text-indigo-700 mb-4">
          Quy Tắc "Ngọn Đồi Số"
        </h2>
        <p className="text-lg mb-4 text-slate-700">
          Để làm tròn số, ta cần xác định <strong>Hàng làm tròn</strong> và quan
          sát <strong>Chữ số ngay sau nó</strong>.
        </p>

        {/* Hill Visualization */}
        <div className="relative h-64 bg-gradient-to-b from-sky-200 to-green-100 rounded-xl overflow-hidden flex items-end justify-center mb-6 border-2 border-indigo-100">
          {/* Hill SVG */}
          <svg
            viewBox="0 0 400 200"
            className="absolute bottom-0 w-full h-full"
            preserveAspectRatio="none"
          >
            <path
              d="M0,200 L100,200 Q200,50 300,200 L400,200"
              fill="#4ade80"
              stroke="#16a34a"
              strokeWidth="3"
            />
            <path
              d="M0,200 L100,200 Q200,50 200,50"
              fill="none"
              stroke="#ef4444"
              strokeWidth="4"
              strokeDasharray="5,5"
            />
            <path
              d="M200,50 Q300,200 400,200"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="4"
              strokeDasharray="5,5"
            />
          </svg>

          {/* Numbers on the hill */}
          <div className="absolute inset-0 flex justify-between items-end px-10 pb-4 text-xl font-bold text-slate-700">
            <div className="absolute bottom-2 left-[10%] bg-white px-3 py-1 rounded shadow text-sm md:text-base">
              Về Số Cũ (Giữ Nguyên)
            </div>
            <div className="absolute bottom-2 right-[10%] bg-white px-3 py-1 rounded shadow text-sm md:text-base">
              Lên Số Mới (+1)
            </div>
          </div>

          {/* Digits positions */}
          <div className="absolute top-[60%] left-[20%] font-bold text-red-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            0
          </div>
          <div className="absolute top-[50%] left-[26%] font-bold text-red-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            1
          </div>
          <div className="absolute top-[40%] left-[32%] font-bold text-red-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            2
          </div>
          <div className="absolute top-[30%] left-[38%] font-bold text-red-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            3
          </div>
          <div className="absolute top-[25%] left-[44%] font-bold text-red-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            4
          </div>

          <div className="absolute top-[25%] right-[44%] font-bold text-blue-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            5
          </div>
          <div className="absolute top-[30%] right-[38%] font-bold text-blue-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            6
          </div>
          <div className="absolute top-[40%] right-[32%] font-bold text-blue-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            7
          </div>
          <div className="absolute top-[50%] right-[26%] font-bold text-blue-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            8
          </div>
          <div className="absolute top-[60%] right-[20%] font-bold text-blue-600 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
            9
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-red-50 p-4 rounded-xl border border-red-200">
            <h3 className="font-bold text-red-600 flex items-center gap-2 text-lg">
              <span className="text-2xl">🛑</span> Nhóm Yếu (0, 1, 2, 3, 4)
            </h3>
            <p className="mt-2 text-slate-700">
              Nếu chữ số sau hàng làm tròn bé hơn 5:
              <br />
              👉 <strong>Giữ nguyên</strong> chữ số hàng làm tròn.
              <br />
              👉 Các số phía sau hàng làm tròn thay bằng số 0 (hoặc bỏ đi nếu ở
              phần thập phân).
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
            <h3 className="font-bold text-blue-600 flex items-center gap-2 text-lg">
              <span className="text-2xl">🚀</span> Nhóm Mạnh (5, 6, 7, 8, 9)
            </h3>
            <p className="mt-2 text-slate-700">
              Nếu chữ số sau hàng làm tròn từ 5 trở lên:
              <br />
              👉 <strong>Cộng thêm 1</strong> vào chữ số hàng làm tròn.
              <br />
              👉 Các số phía sau hàng làm tròn thay bằng số 0 (hoặc bỏ đi nếu ở
              phần thập phân).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- 2. Explore Section: Interactive Number Line ---

function ExploreSection() {
  const [inputStr, setInputStr] = useState<string>("1234,567"); // Sử dụng chuỗi để quản lý input
  const [place, setPlace] = useState<string>("tenth");
  const [animationClass, setAnimationClass] = useState("");

  const number = parseNumber(inputStr); // Giá trị số thực tế để tính toán
  const rounded = roundToPlace(number, place);
  const { floor, ceil } = getFloorCeil(number, place);

  // Logic tìm chữ số quyết định (Deciding Digit)
  // Cần xử lý cẩn thận vì vị trí dấu phẩy thay đổi
  const getDecidingDigitInfo = () => {
    // Để xác định chính xác, ta nhân số lên để hàng cần xét trở thành hàng đơn vị
    // Ví dụ: 12.345 làm tròn phần mười. Nhân 10 -> 123.45.
    // Chữ số quyết định là phần thập phân đầu tiên: .4 -> 4.

    let shift = 0;
    switch (place) {
      case "thousand":
        shift = -3;
        break;
      case "hundred":
        shift = -2;
        break;
      case "ten":
        shift = -1;
        break;
      case "integer":
        shift = 0;
        break;
      case "tenth":
        shift = 1;
        break;
      case "hundredth":
        shift = 2;
        break;
      case "thousandth":
        shift = 3;
        break;
    }

    // Lấy chữ số ngay sau hàng làm tròn
    // Ví dụ: num = 1234.567, place = ten (hàng chục, số 3).
    // shift = -1. num * 10^-1 = 123.4567.
    // Ta cần chữ số sau hàng đơn vị của số đã shift, tức là phần thập phân đầu tiên.

    const shiftedNum = number * Math.pow(10, shift);
    // Lấy phần thập phân của số đã shift.
    // Ví dụ 123.4567 -> lấy .4
    // Math.abs để xử lý số âm nếu có
    const decimalPart = Math.abs(shiftedNum) - Math.floor(Math.abs(shiftedNum));
    // Lấy chữ số đầu tiên sau dấu phẩy: 0.4567 * 10 = 4.567 -> floor -> 4
    const decidingDigit = Math.floor(decimalPart * 10 + 0.00001); // + epsilon để tránh lỗi float

    const isRoundingUp = decidingDigit >= 5;
    return { decidingDigit, isRoundingUp };
  };

  const { decidingDigit, isRoundingUp } = getDecidingDigitInfo();

  // Visualization Percentage
  let percent = 50;
  if (ceil !== floor) {
    percent = ((number - floor) / (ceil - floor)) * 100;
  }
  // Clamp percent
  percent = Math.max(0, Math.min(100, percent));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Cho phép nhập dấu phẩy hoặc chấm, nhưng hiển thị là phẩy
    // Chỉ cho phép số, dấu phẩy, dấu chấm, dấu trừ
    if (/^[\d,.-]*$/.test(val)) {
      // Tự động chuyển chấm thành phẩy ngay khi nhập
      val = val.replace(/\./g, ",");
      setInputStr(val);
    }
  };

  useEffect(() => {
    setAnimationClass("scale-110 text-indigo-600 font-bold");
    const timer = setTimeout(() => setAnimationClass(""), 300);
    return () => clearTimeout(timer);
  }, [rounded]);

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md border-l-8 border-green-500 animate-fade-in">
      <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center gap-2">
        <Calculator /> Mô Hình Tương Tác
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Nhập số (dùng dấu phẩy):
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={inputStr}
              onChange={handleInputChange}
              className="w-full text-3xl font-bold p-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-center tracking-wider"
              placeholder="Ví dụ: 12,5"
            />
            {inputStr && (
              <button
                onClick={() => setInputStr("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 p-2"
                title="Xoá hết"
              >
                <Delete size={20} />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center italic">
            Bạn có thể xoá và nhập số bất kỳ
          </p>
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">
            Chọn hàng làm tròn:
          </label>
          <div className="space-y-3">
            {/* Nhóm phần nguyên */}
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setPlace("thousand")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "thousand"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                H.Nghìn
              </button>
              <button
                onClick={() => setPlace("hundred")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "hundred"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                H.Trăm
              </button>
              <button
                onClick={() => setPlace("ten")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "ten"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                H.Chục
              </button>
              <button
                onClick={() => setPlace("integer")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "integer"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                Đơn vị
              </button>
            </div>
            {/* Nhóm phần thập phân */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setPlace("tenth")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "tenth"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                Phần mười
              </button>
              <button
                onClick={() => setPlace("hundredth")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "hundredth"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                Phần trăm
              </button>
              <button
                onClick={() => setPlace("thousandth")}
                className={`py-2 px-1 text-sm rounded-lg border-2 font-medium ${
                  place === "thousandth"
                    ? "bg-green-100 border-green-500 text-green-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                Phần nghìn
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Visualizer Area */}
      <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-6 relative">
        <div className="flex justify-between text-lg text-gray-500 font-medium mb-1">
          <span>{formatNumber(floor)}</span>
          <span>{formatNumber(ceil)}</span>
        </div>

        {/* Progress Bar Container */}
        <div className="h-6 bg-gray-200 rounded-full relative shadow-inner">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-300 to-indigo-400 rounded-full transition-all duration-500"
            style={{ width: `${percent}%` }}
          ></div>

          {/* Midpoint Marker */}
          <div className="absolute top-[-5px] left-1/2 w-0.5 h-8 bg-red-400 z-0"></div>
          <div className="absolute top-[-20px] left-1/2 -translate-x-1/2 text-xs text-red-400 font-bold">
            5
          </div>

          {/* The "Ball" (Current Number) */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full border-4 border-indigo-600 shadow-xl flex items-center justify-center transition-all duration-500 z-10"
            style={{ left: `calc(${percent}% - 20px)` }}
          >
            <span className="text-[10px] text-indigo-900 font-black">XE</span>
          </div>
        </div>

        {/* Guide Text */}
        <div className="mt-8 text-center bg-white p-4 rounded-lg shadow-sm border border-slate-100">
          <p className="text-gray-600 mb-2">
            Chữ số quyết định (sau hàng làm tròn) là:{" "}
            <span className="inline-block w-8 h-8 bg-yellow-300 text-yellow-900 rounded-full font-bold pt-1 border border-yellow-400 shadow-sm">
              {decidingDigit}
            </span>
          </p>
          <p className="text-lg">
            Vì{" "}
            <strong>
              {decidingDigit} {isRoundingUp ? "≥ 5" : "< 5"}
            </strong>{" "}
            nên xe trượt về phía số:{" "}
            <strong
              className={isRoundingUp ? "text-green-600" : "text-red-600"}
            >
              {isRoundingUp ? "LỚN (Lên)" : "BÉ (Xuống)"}
            </strong>
          </p>
        </div>
      </div>

      {/* Result */}
      <div className="text-center bg-green-50 p-6 rounded-xl border-2 border-green-200 flex flex-col items-center">
        <div className="text-green-800 font-medium uppercase tracking-wide text-sm mb-2">
          Kết quả làm tròn
        </div>
        <div
          className={`text-4xl md:text-5xl transition-all duration-300 ${animationClass}`}
        >
          {formatNumber(rounded)}
        </div>
        <div className="mt-2 text-green-600 text-sm">
          ({formatNumber(number)} ≈ {formatNumber(rounded)})
        </div>
      </div>
    </div>
  );
}

// --- 3. Practice Section: Mini Game ---

function PracticeSection() {
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [question, setQuestion] = useState<any>(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState<"correct" | "incorrect" | null>(
    null
  );
  const [showSolution, setShowSolution] = useState(false);

  const generateQuestion = () => {
    const places = ["integer", "tenth", "hundredth"];
    const placeNames = {
      integer: "hàng đơn vị",
      tenth: "hàng phần mười",
      hundredth: "hàng phần trăm",
    };

    const randomPlace = places[Math.floor(Math.random() * places.length)];
    // Generate number with 3 decimal places
    const randomNum = (Math.random() * 100).toFixed(3);
    const num = parseFloat(randomNum);

    setQuestion({
      number: num,
      place: randomPlace,
      placeName: placeNames[randomPlace as keyof typeof placeNames],
      correct: roundToPlace(num, randomPlace),
    });
    setUserAnswer("");
    setFeedback(null);
    setShowSolution(false);
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const checkAnswer = () => {
    if (!question) return;
    // User nhập dấu phẩy, cần parse
    const val = parseNumber(userAnswer);
    if (Math.abs(val - question.correct) < 0.0001) {
      // So sánh float an toàn
      setFeedback("correct");
      setScore(score + 10);
      setStreak(streak + 1);
    } else {
      setFeedback("incorrect");
      setStreak(0);
    }
    setShowSolution(true);
  };

  const handleNext = () => {
    generateQuestion();
  };

  const handleInputAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (/^[\d,.-]*$/.test(val)) {
      val = val.replace(/\./g, ",");
      setUserAnswer(val);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md border-l-8 border-orange-500 animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-orange-600 flex items-center gap-2">
          <Brain /> Thử Thách Nhí
        </h2>
        <div className="flex gap-4">
          <div className="bg-yellow-100 text-yellow-800 px-4 py-1 rounded-full font-bold text-sm md:text-base">
            Điểm: {score}
          </div>
          <div className="hidden md:flex bg-orange-100 text-orange-800 px-4 py-1 rounded-full font-bold items-center gap-1">
            🔥 Chuỗi: {streak}
          </div>
        </div>
      </div>

      {question && (
        <div className="text-center py-8">
          <p className="text-xl text-gray-600 mb-2">Hãy làm tròn số:</p>
          <div className="text-5xl font-black text-slate-800 mb-4 tracking-wider">
            {formatNumber(question.number)}
          </div>
          <p className="text-xl text-gray-600 mb-8">
            đến{" "}
            <span className="font-bold text-indigo-600 border-b-2 border-indigo-400">
              {question.placeName}
            </span>
          </p>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 max-w-md mx-auto">
            <input
              type="text"
              inputMode="decimal"
              value={userAnswer}
              onChange={handleInputAnswerChange}
              placeholder="Nhập..."
              className="text-center text-2xl p-3 border-2 border-gray-300 rounded-xl w-48 focus:border-orange-500 focus:outline-none"
              disabled={showSolution}
              onKeyDown={(e) =>
                e.key === "Enter" && !showSolution && checkAnswer()
              }
            />
            {!showSolution ? (
              <button
                onClick={checkAnswer}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors shadow-lg shadow-orange-200 w-full md:w-auto"
              >
                Kiểm Tra
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2 w-full md:w-auto"
              >
                Câu Tiếp <ArrowRight size={20} />
              </button>
            )}
          </div>

          {/* Feedback Area */}
          {showSolution && (
            <div
              className={`mt-8 p-6 rounded-xl animate-bounce-in ${
                feedback === "correct"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {feedback === "correct" ? (
                <div className="flex flex-col items-center gap-2">
                  <CheckCircle size={48} className="text-green-500" />
                  <span className="text-2xl font-bold">
                    Chính xác! Giỏi quá! 🎉
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <XCircle size={48} className="text-red-500" />
                  <span className="text-2xl font-bold">Ồ, tiếc quá! 😅</span>
                  <p className="text-lg">
                    Đáp án đúng là:{" "}
                    <span className="font-bold text-3xl mx-2">
                      {formatNumber(question.correct)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
